
import { useCallback, useState, Dispatch, SetStateAction } from 'react';
import { AppNode, ProcessDefinition, Edge, Swimlane, NamedRuleSet } from '../types';
import { saveProjectToFile, loadProjectWithRuleSets } from '../utils/pssHelper';
import { importBpmnXml, detectBpmnFormat } from '../utils/bpmnHelper';
import { validateDiagram, summarize, hasBlockingErrors } from '../utils/validation';
import type { ValidationIssue } from '../utils/validation';
// Fix: Use named import for useReactFlow from reactflow.
import { useReactFlow } from 'reactflow';
import { useToast } from '../contexts/ToastContext';
import { BYTES_PER_MB } from '../utils/layoutConstants';

// Vite's `import.meta.env.DEV` flag without pulling in vite/client types.
// Matches the pattern used in utils/validation/registry.ts.
const IS_DEV =
    typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;

export interface UseFileOperationsOptions {
    nodes: AppNode[];
    edges: Edge[];
    processes: ProcessDefinition[];
    setProcesses: Dispatch<SetStateAction<ProcessDefinition[]>>;
    activeProcessId: string;
    setActiveProcessId: Dispatch<SetStateAction<string>>;
    setNodes: Dispatch<SetStateAction<AppNode[]>>;
    setEdges: Dispatch<SetStateAction<Edge[]>>;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    swimlanes?: Swimlane[];
    setSwimlanes?: Dispatch<SetStateAction<Swimlane[]>>;
    ruleSets?: NamedRuleSet[];
    setRuleSets?: Dispatch<SetStateAction<NamedRuleSet[]>>;
}

export const useFileOperations = (options: UseFileOperationsOptions) => {
    const {
        nodes,
        edges,
        processes,
        setProcesses,
        activeProcessId,
        setActiveProcessId,
        setNodes,
        setEdges,
        setIsLoading,
        swimlanes = [],
        setSwimlanes,
        ruleSets = [],
        setRuleSets,
    } = options;
    const { fitView } = useReactFlow();
    const { addToast } = useToast();
    const [projectName, setProjectName] = useState("Peak_Workflow_Project");

    const [lastValidationIssues, setLastValidationIssues] = useState<ValidationIssue[]>([]);

    const handleSaveProject = useCallback(async (options?: { bypassValidation?: boolean }) => {
        setIsLoading(true);
        try {
            const allProcesses = processes.map(p =>
                p.id === activeProcessId ? { ...p, nodes, edges, swimlanes } : p
            );

            const issues = validateDiagram(allProcesses, ruleSets);
            setLastValidationIssues(issues);
            const { errors, warnings } = summarize(issues);

            if (hasBlockingErrors(issues) && !options?.bypassValidation) {
                if (IS_DEV) {
                    console.warn('[validation] Save blocked.', issues);
                }
                addToast({
                    type: 'error',
                    title: 'Save Blocked',
                    message: `${errors} error${errors === 1 ? '' : 's'}${warnings ? ` + ${warnings} warning${warnings === 1 ? '' : 's'}` : ''} detected. Fix errors before saving.`,
                });
                return { ok: false as const, issues };
            }

            saveProjectToFile(allProcesses, projectName, ruleSets);
            if (warnings > 0) {
                addToast({
                    type: 'warning',
                    title: 'Saved with Warnings',
                    message: `Exported with ${warnings} warning${warnings === 1 ? '' : 's'}.`,
                });
            } else {
                addToast({ type: 'success', title: 'Project Saved', message: 'Workflow project exported successfully.' });
            }
            return { ok: true as const, issues };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Could not export project.';
            addToast({ type: 'error', title: 'Save Failed', message });
            return { ok: false as const, issues: [] };
        } finally {
            setIsLoading(false);
        }
    }, [nodes, edges, swimlanes, processes, activeProcessId, projectName, ruleSets, addToast, setIsLoading]);

    const handleLoadProject = useCallback(async (file: File) => {
        const name = file.name.toLowerCase();
        const isPss = name.endsWith('.bpjs') || name.endsWith('.json');
        const isBpmn = name.endsWith('.bpmn');
        const isXml = name.endsWith('.xml');

        if (!isPss && !isBpmn && !isXml) {
            addToast({ type: 'error', title: 'Invalid File Type', message: 'Supported formats: .bpjs, .json, .bpmn, .xml' });
            return;
        }

        const MAX_SIZE_MB = 10;
        if (file.size > MAX_SIZE_MB * BYTES_PER_MB) {
            addToast({ type: 'error', title: 'File Too Large', message: `File exceeds ${MAX_SIZE_MB}MB limit.` });
            return;
        }

        setIsLoading(true);
        try {
            let loadedProcesses: ProcessDefinition[];

            if (isPss) {
                const { processes: loaded, ruleSets: loadedRuleSets } = await loadProjectWithRuleSets(file);
                loadedProcesses = loaded;
                if (setRuleSets) setRuleSets(loadedRuleSets);
            } else {
                const text = await file.text();

                if (isXml && !detectBpmnFormat(text)) {
                    addToast({ type: 'error', title: 'Invalid File', message: 'XML file does not appear to be a BPMN 2.0 document.' });
                    return;
                }

                const { processes, warnings } = importBpmnXml(text);
                loadedProcesses = processes;

                if (warnings.length > 0 && IS_DEV) {
                    console.warn('[BPMN Import] Warnings:', warnings);
                }
            }

            if (!loadedProcesses || loadedProcesses.length === 0) {
                addToast({ type: 'error', title: 'Import Failed', message: "No valid processes found in file." });
                return;
            }

            const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');
            setProjectName(nameWithoutExt);

            setProcesses(loadedProcesses);

            const firstProc = loadedProcesses[0];
            setNodes(firstProc.nodes);
            setEdges(firstProc.edges);
            if (setSwimlanes) setSwimlanes(firstProc.swimlanes || []);
            setActiveProcessId(firstProc.id);

            setTimeout(() => fitView(), 100);
            addToast({ type: 'success', title: 'Project Loaded', message: `Successfully loaded ${loadedProcesses.length} process(es).` });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load project file.';
            console.error('Failed to load project:', error);
            addToast({ type: 'error', title: 'Import Error', message });
            return;
        } finally {
            setIsLoading(false);
        }
    }, [setNodes, setEdges, setProcesses, setActiveProcessId, fitView, addToast, setIsLoading, setSwimlanes, setRuleSets]);

    return {
        handleSaveProject,
        handleLoadProject,
        projectName,
        validationIssues: lastValidationIssues,
    };
};